/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from "react";

import { Unauthorized } from "@/src/error-handling/Unauthorized/Unauthorized";
import { useQueryClientCustom } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import type { IErrorMessage } from "@Components/ErrorMessage";
import { ErrorMessage } from "@Components/ErrorMessage";

import { urlFormat } from "../Modules/Helpers";

export interface Output<T = void> {
    success: boolean;
    error?: string;
    data: T;
}

export enum AjaxMethod {
    Get,
    Post,
}

export type AjaxSuccess<TResult extends object | string | boolean | number | void> = (
    response: TResult | undefined,
) => void;

export type AjaxFail = (response: any, xhr?: XMLHttpRequest) => void;

export type IAjax<TResult extends object | string | boolean | number | void> = {
    ajaxAction?: string;
    ajaxMethod?: AjaxMethod;
    initialData?: TResult;
    success?: AjaxSuccess<TResult> | undefined;
    fail?: AjaxFail | undefined;
    onErrorContainerClose?: () => boolean | void;
};

const get = (
    url: string,
    requestOnLoad: (xhr: XMLHttpRequest) => void,
    headers?: { [key: string]: string },
): XMLHttpRequest => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Accept-Images", browserStatics.imageSupport);

    if (headers) {
        Object.keys(headers).forEach((k) => xhr.setRequestHeader(k, headers[k]));
    }

    requestOnLoad(xhr);
    xhr.send();
    return xhr;
};

const post = (
    url: string,
    params: object | undefined,
    requestOnLoad: (xhr: XMLHttpRequest) => void,
    headers?: { [key: string]: string },
): XMLHttpRequest => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", url, true);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.setRequestHeader("Accept-Images", browserStatics.imageSupport);

    if (headers) {
        Object.keys(headers).forEach((k) => xhr.setRequestHeader(k, headers[k]));
    }

    let urlEncodedData: string | null = null;
    xhr.setRequestHeader("Content-Type", "application/json; charset=utf-8");

    if (params) {
        urlEncodedData = JSON.stringify(params);
    }

    requestOnLoad(xhr);

    xhr.send(urlEncodedData);
    return xhr;
};

const parseResponse: <TResult>(xhr: XMLHttpRequest) => TResult | undefined = (xhr) => {
    if (!xhr) {
        return undefined;
    }

    if (!xhr.responseText) {
        return undefined;
    }

    try {
        return JSON.parse(xhr.responseText);
    } catch (err) {
        console.error(err);
        return xhr.responseText;
    }
};

const executeRequest = (
    ajaxAction: string | undefined,
    ajaxMethod: AjaxMethod | undefined,
    urlParams: object | undefined,
    postParams: object | undefined,
    requestOnLoad: (_xhr: XMLHttpRequest) => void,
    headers?: { [key: string]: string },
): XMLHttpRequest | undefined => {
    if (!ajaxAction) {
        return undefined;
    }

    const url = urlFormat(ajaxAction, urlParams);

    if (!url) {
        return undefined;
    }

    switch (ajaxMethod) {
        case AjaxMethod.Post:
            return post(url, postParams, requestOnLoad, headers);
        case AjaxMethod.Get:
        default:
            return get(url, requestOnLoad, headers);
    }
};

const isErrorResponse = (response: any): response is IErrorMessage =>
    response && response.errorName !== null && response.errorName !== undefined;

interface IUseAjaxState<TResult> {
    isLoading: boolean;
    requestXhr?: XMLHttpRequest;
    data?: TResult;
    errorContainer?: JSX.Element;
}

export default <TResult extends object | string | boolean | number | void>(props: IAjax<TResult>) => {
    const queryClient = useQueryClientCustom();
    const [{ isLoading, requestXhr, data, errorContainer }, setState] = useState<IUseAjaxState<TResult>>({
        isLoading: false,
        data: props.initialData,
    });

    const handleSuccess = (xhr: XMLHttpRequest): void => {
        const result = parseResponse<TResult>(xhr);

        setState((currentState) => ({ ...currentState, isLoading: false, data: result }));

        if (props.success) {
            props.success(result);
        }
    };

    const prepareFailResult = (response: TResult | string | undefined, xhr: XMLHttpRequest) => {
        let errorMessage: JSX.Element | undefined;

        if (response === "timeout") {
            errorMessage = <ErrorMessage message="Timeout" onClose={props.onErrorContainerClose} />;
        } else if (xhr.status === 401 || xhr.status === 403) {
            queryClient.invalidateQueries();
            errorMessage = (
                <Unauthorized
                    onClose={() => {
                        if (!props.onErrorContainerClose || props.onErrorContainerClose() !== false) {
                            setState((currentState) => ({ ...currentState, errorContainer: null }));
                        }
                    }}
                />
            );
        } else if (isErrorResponse(response) === true) {
            errorMessage = (
                <ErrorMessage {...(response as IErrorMessage)} onClose={props.onErrorContainerClose} />
            );
        }

        setState((currentState) => ({
            ...currentState,
            isLoading: false,
            errorContainer: errorMessage,
        }));

        if (props.fail) {
            props.fail(response, xhr);
        }
    };

    const handleFail = (xhr: XMLHttpRequest): void => prepareFailResult(parseResponse(xhr), xhr);

    const handleTimeout = (xhr: XMLHttpRequest): void => prepareFailResult("timeout", xhr);

    const request = (urlParams?: object, postParams?: object, headers?: { [key: string]: string }): void => {
        const requestOnLoad = (xhr: XMLHttpRequest): void => {
            if (!xhr) {
                return;
            }

            setState((currentState) => ({ ...currentState, isLoading: true, requestXhr: xhr }));

            xhr.onload = () => (xhr.status === 200 ? handleSuccess(xhr) : handleFail(xhr));

            xhr.ontimeout = () => handleTimeout(xhr);
            xhr.onerror = () => handleFail(xhr);
        };

        executeRequest(props.ajaxAction, props.ajaxMethod, urlParams, postParams, requestOnLoad, headers);
    };

    useEffect(() => () => requestXhr?.abort(), [requestXhr]);

    return {
        data,
        isLoading,
        request,
        errorContainer,
    };
};
